/**
 * Mini Moto (#4577): the contract between the room and the game iframe.
 *
 * The game runs in a same-origin iframe (D10). The room page and the game talk
 * over `postMessage`, and every message in either direction is checked by the
 * parsers below before anything acts on it. A message that fails a check is
 * dropped, never repaired.
 *
 * Spec: `_docs/Q4/MiniMoto/MiniMoto-FunctionalSpec.md`.
 */

export const MINI_MOTO_ACTIVITY_ID = 'mini-moto';
/** Bumped when a message changes shape; the game reports it in `ready`. */
export const MINI_MOTO_BRIDGE_VERSION = 1;
/** The game's grid is fixed at eight riders. */
export const MINI_MOTO_GRID_SIZE = 8;
/** Peer-to-peer pose fan-out caps the humans on track (D4). */
export const MINI_MOTO_MAX_HUMANS = 6;
export const MINI_MOTO_LAP_OPTIONS = [3, 5, 8] as const;
export const MINI_MOTO_DEFAULT_LAPS = 3;
export const MINI_MOTO_POSE_HZ = 20;
export const MINI_MOTO_RIDER_NAME_MAX = 16;
/**
 * The game's own roster names, by grid slot (`bq` in the recovered
 * `game-03.js`). AI riders keep them, so the booth and the HUD agree.
 */
export const MINI_MOTO_AI_RIDER_NAMES = ['REX', 'NOVA', 'FINN', 'ARIA', 'JAX', 'MILO', 'KAI', 'LUNA'] as const;
/** The game's built-in map themes (`d4` in the recovered `game-01.js`), shown by these names. */
export const MINI_MOTO_THEMES = {
  pine: 'Pine Ridge',
  beach: 'Beach',
  desert: 'Desert',
  cyberpunk: 'Cyberpunk',
  tokyo: 'Tokyo',
  florida: 'Florida',
  mushroom: 'Mushroom',
  stadium: 'Stadium',
  kitchen: 'Kitchen',
} as const;
export type MiniMotoTheme = keyof typeof MINI_MOTO_THEMES;
/** The game's park holds at most 25 map tiles. */
export const MINI_MOTO_MAX_TILES = 25;

/**
 * One map tile of the track (#4584). A track is an ordered list: the first
 * tile is always at 0,0 and each later one is attached next to an earlier one,
 * which is how the game itself builds and restores a park.
 */
export interface MiniMotoTile {
  gx: number;
  gz: number;
  theme: MiniMotoTheme;
}

/** The game's own starting park: one Pine Ridge map. */
export const MINI_MOTO_DEFAULT_COURSE: readonly MiniMotoTile[] = [{ gx: 0, gz: 0, theme: 'pine' }];

/** The game's vehicle choices (#4584): what everyone rides, and how big. The host picks between races. */
export const MINI_MOTO_VEHICLE_MODES = { bikes: 'Bikes', karts: 'Karts', mixed: 'Mixed' } as const;
/** Standard is the game's full-size machine (100%), Small 55%, Tiny the game's own RC-scale default (30%). Smaller riders read the same track as wider, with gentler corners. */
export const MINI_MOTO_VEHICLE_SCALES = { standard: 'Standard', small: 'Small', tiny: 'Tiny' } as const;
export type MiniMotoVehicleMode = keyof typeof MINI_MOTO_VEHICLE_MODES;
export type MiniMotoVehicleScale = keyof typeof MINI_MOTO_VEHICLE_SCALES;
export interface MiniMotoVehicles {
  mode: MiniMotoVehicleMode;
  scale: MiniMotoVehicleScale;
}
/** Full-size bikes. (The game's own default is tiny bikes.) */
/** A new Mini Moto table races tiny karts (owner's call, 2026-10-18); the host can change it in the lobby. */
export const MINI_MOTO_DEFAULT_VEHICLES: MiniMotoVehicles = { mode: 'karts', scale: 'tiny' };
/** A race id is a short opaque token chosen by the race manager. */
const RACE_ID = /^[A-Za-z0-9_-]{1,64}$/;
/** The course is a box roughly 105 × 77 m; anything far outside it is not a pose. */
const COORD_LIMIT = 1000;
const SPEED_LIMIT = 200;
/** Race-clock seconds; eight long laps finish well inside an hour. */
const CLOCK_LIMIT = 3600;
const MAX_GATE_DELAY_MS = 60_000;
/** A client that loads mid-race is told how long ago the gate dropped (negative gateInMs). */
const MAX_GATE_ELAPSED_MS = 2 * 3600_000;
const MAX_EVENTS_PER_BATCH = 64;
const MAX_FORWARD_KEYS = 8;
const KEY_CODE = /^[A-Za-z0-9]{1,24}$/;
const MESSAGE_MAX = 160;
/** C0/C1 controls, zero-width and bidi controls, line/paragraph separators, markup brackets. */
const UNSAFE_NAME_CHARS = /[\u0000-\u001f\u007f-\u009f\u200b-\u200f\u2028-\u202e\u2066-\u2069<>]/g;

export type MiniMotoLaps = (typeof MINI_MOTO_LAP_OPTIONS)[number];
export type MiniMotoSlotKind = 'human' | 'ai';
/** What the local client shows: its own bike, the pack, or the action. */
export type MiniMotoCamera = 'ride' | 'follow' | 'battle';

export interface MiniMotoSlot {
  /** Grid slot, 0..7. It is also the game's rider id. */
  slot: number;
  kind: MiniMotoSlotKind;
  /** Shown in the standings and spoken by the booth. Already sanitised. */
  name: string;
}

/** Room → game: set up a race. Replaces any race in progress. */
export interface MiniMotoRaceMessage {
  type: 'race';
  raceId: string;
  /** Same seed on every client, so every client draws the same grid. */
  seed: number;
  laps: MiniMotoLaps;
  /**
   * Milliseconds from receipt until the gate drops. The room converts the
   * server's gate time through its own clock; the iframe's clocks are its own.
   * Negative when the gate has already dropped (a client that loaded late):
   * the game then starts its race clock that far in.
   */
  gateInMs: number;
  slots: MiniMotoSlot[];
  /** The shared track (#4584). Every client races on exactly this. */
  course: MiniMotoTile[];
  vehicles: MiniMotoVehicles;
  /** The slot this client rides, or null for a spectator. */
  localSlot: number | null;
  /** True on the one client that simulates the AI riders and reports standings. */
  authority: boolean;
  camera: MiniMotoCamera;
  /**
   * A spectator's camera rides behind this human rider instead of roaming the pack: the room's TV
   * follows the person racing. Absent or null: the race camera picks its own shots.
   */
  watchSlot?: number | null;
  /** Key codes the game must hand back to the room (push-to-talk). */
  forwardKeys: string[];
}

/** One rider's state at a race-clock instant. Travels room ↔ game and peer ↔ peer. */
export interface MiniMotoPose {
  raceId: string;
  slot: number;
  /** Per-sender counter; receivers drop anything not newer. */
  seq: number;
  /** Race clock, seconds since the gate. 0 on the grid. */
  t: number;
  p: [number, number, number];
  q: [number, number, number, number];
  v: [number, number, number];
  lap: number;
  /** Metres along the race distance; negative behind the line on the grid. */
  distance: number;
  finished: boolean;
  /** Race-clock seconds; 0 until finished. */
  finishTime: number;
}

export interface MiniMotoPoseMessage extends MiniMotoPose {
  type: 'pose';
}

/** Room → game: the race was called off (D11). */
export interface MiniMotoAbortMessage {
  type: 'abort';
  raceId: string;
}

/**
 * Room -> game between races (#4584): show the room's track, and let only the
 * host extend it with the game's own ADD MAP picker.
 */
export interface MiniMotoLobbyMessage {
  type: 'lobby';
  course: MiniMotoTile[];
  vehicles: MiniMotoVehicles;
  editor: boolean;
}

/** Room -> game: the track editor's tool, removal on or off (the game's `remove` / `watch` tools). */
export interface MiniMotoToolMessage {
  type: 'tool';
  remove: boolean;
}

/** Room -> game: this client is (or is no longer) the foam cannon's gunner (#4586). */
export interface MiniMotoCannonMessage {
  type: 'cannon';
  gunner: boolean;
}

/** Room -> game: the gunner's turret aim, and whether it fired, as seen by this client's turret. */
export interface MiniMotoCannonReplayMessage extends MiniMotoCannonAim {
  type: 'cannon-replay';
}

/** Room -> game: a foam hit landed on `slot`; the rider's owner applies the spin-out, everyone shows it. */
export interface MiniMotoFoamMessage {
  type: 'foam';
  slot: number;
}

/** The game's hold-to-ride inputs (`setDriveInput`); its own touch buttons use the same four. */
export const MINI_MOTO_DRIVE_INPUTS = ['left', 'brake', 'throttle', 'right'] as const;
export type MiniMotoDriveInput = (typeof MINI_MOTO_DRIVE_INPUTS)[number];

/**
 * Room -> game: an on-screen riding control is held or released. Room mode hides the game's
 * own touch buttons with the rest of its dock, so the room draws its own for touch devices.
 */
export interface MiniMotoDriveMessage {
  type: 'drive';
  input: MiniMotoDriveInput;
  held: boolean;
}

/**
 * Room -> game: analog steering from the TV remote's virtual joystick, -1 (full left) to 1 (full
 * right), 0 to let go. The game steers by degrees (its gamepad axis), not only left or right.
 */
export interface MiniMotoSteerMessage {
  type: 'steer';
  value: number;
}

/** How far behind and above the chase camera rides: three third-person distances. */
export const MINI_MOTO_CHASE_DISTANCES = ['near', 'mid', 'far'] as const;
export type MiniMotoChaseDistance = (typeof MINI_MOTO_CHASE_DISTANCES)[number];
/** A player's own screen starts at Far, the widest view (owner's call, 2026-10-18). */
export const MINI_MOTO_DEFAULT_CHASE: MiniMotoChaseDistance = 'far';
/** The room's TV starts at Far too. */
export const MINI_MOTO_DEFAULT_TV_CHASE: MiniMotoChaseDistance = 'far';

/** Room -> game: the chase camera's distance on this client. */
export interface MiniMotoChaseMessage {
  type: 'chase';
  distance: MiniMotoChaseDistance;
}

export function isMiniMotoChaseDistance(value: unknown): value is MiniMotoChaseDistance {
  return (MINI_MOTO_CHASE_DISTANCES as readonly unknown[]).includes(value);
}

/**
 * The tabletop look's camera finish (#4604): tilt-shift on or off, and its settings. The host
 * tunes it from the lobby for their own view; it is not shared with the room.
 * focusScale multiplies the automatic focus on the orbit target (below 1 is nearer the camera);
 * band is the sharp zone around it as a fraction of distance; blur the widest blur as a fraction
 * of frame height; strength how fast blur grows; fov the park views' field of view in degrees,
 * or null for the game's own.
 */
export interface MiniMotoLook {
  tiltShift: boolean;
  focusScale: number;
  band: number;
  blur: number;
  strength: number;
  fov: number | null;
  saturation: number;
  vignette: number;
}

export const MINI_MOTO_LOOK_DEFAULTS: MiniMotoLook = {
  // Off by default (owner's call, 2026-10-18); the host can still turn it on from the lobby.
  tiltShift: false,
  focusScale: 1,
  band: 0.05,
  blur: 0.009,
  strength: 2.6,
  fov: null,
  saturation: 1.14,
  vignette: 0.32,
};

/** Slider ranges for the look's numbers; `parseMiniMotoLook` clamps into them. */
export const MINI_MOTO_LOOK_RANGES = {
  focusScale: { min: 0.5, max: 1.5, step: 0.01 },
  band: { min: 0, max: 0.3, step: 0.01 },
  blur: { min: 0.002, max: 0.025, step: 0.001 },
  strength: { min: 0.5, max: 8, step: 0.1 },
  fov: { min: 15, max: 75, step: 1 },
  saturation: { min: 0.8, max: 1.5, step: 0.01 },
  vignette: { min: 0, max: 0.8, step: 0.01 },
} as const;

/** Room -> game: the look for this viewer. */
export interface MiniMotoLookMessage {
  type: 'look';
  look: MiniMotoLook;
}

/** A look from storage or the wire: missing or bad fields take the default, numbers are clamped. */
export function parseMiniMotoLook(data: unknown): MiniMotoLook | null {
  if (!isRec(data)) return null;
  const out: MiniMotoLook = { ...MINI_MOTO_LOOK_DEFAULTS };
  if (typeof data.tiltShift === 'boolean') out.tiltShift = data.tiltShift;
  for (const key of Object.keys(MINI_MOTO_LOOK_RANGES) as (keyof typeof MINI_MOTO_LOOK_RANGES)[]) {
    const v = data[key];
    const { min, max } = MINI_MOTO_LOOK_RANGES[key];
    if (typeof v === 'number' && Number.isFinite(v)) out[key] = Math.min(max, Math.max(min, v));
  }
  if (data.fov === null) out.fov = null;
  return out;
}

/** Room -> game: this device is (or is no longer) in lite mode; the game drops its costliest effects (tilt-shift). */
export interface MiniMotoLiteMessage {
  type: 'lite';
  on: boolean;
}

/**
 * Room -> game: draw frames or not. A phone used as the Connected TV remote keeps simulating its
 * rider (and the AI, as authority) and sending poses, but the TV shows the race, so it draws nothing.
 */
export interface MiniMotoRenderMessage {
  type: 'render';
  on: boolean;
}

export type MiniMotoRoomToGameMessage =
  | MiniMotoSteerMessage
  | MiniMotoChaseMessage
  | MiniMotoRenderMessage
  | MiniMotoLookMessage
  | MiniMotoLiteMessage
  | MiniMotoDriveMessage
  | MiniMotoRaceMessage
  | MiniMotoPoseMessage
  | MiniMotoAbortMessage
  | MiniMotoLobbyMessage
  | MiniMotoToolMessage
  | MiniMotoCannonMessage
  | MiniMotoCannonReplayMessage
  | MiniMotoFoamMessage;

/**
 * What happened on track. Each client reports only riders it simulates: a
 * rider's own client for humans, the authority for AI riders.
 */
export type MiniMotoRaceEventKind =
  | 'boost' // detail: source (holeshot, holeshotGood, slingshot, nitro, pad, stomp, scrub, whip, flip)
  | 'release' // spark boost; detail: tier
  | 'landing' // detail: grade (stomped, clean, sketchy, tossed)
  | 'trick'
  | 'spin'
  | 'down'
  | 'knockout'
  | 'bogged'
  | 'tow'
  | 'intent' // AI tactical intent; detail: the game's own words
  | 'lap' // detail: lap, lapTime, bestLap
  | 'finish' // detail: finishTime
  | 'gate' // authority only, once, at the drop
  | 'foam'; // a spectator's foam cannon hit this rider; text: the gunner's name (#4586)

export const MINI_MOTO_RACE_EVENT_KINDS: readonly MiniMotoRaceEventKind[] = [
  'boost', 'release', 'landing', 'trick', 'spin', 'down', 'knockout', 'bogged', 'tow', 'intent', 'lap', 'finish', 'gate', 'foam',
];

export interface MiniMotoRaceEvent {
  kind: MiniMotoRaceEventKind;
  /** Race-clock seconds. */
  t: number;
  /** Rider the event belongs to; -1 for race-wide events. */
  slot: number;
  source?: string;
  grade?: string;
  tier?: number;
  text?: string;
  lap?: number;
  lapTime?: number;
  bestLap?: number;
  finishTime?: number;
}

/** Authority only: the race as the authority sees it, a few times a second. */
export interface MiniMotoStandings {
  raceId: string;
  t: number;
  /** Slots, leader first. */
  order: number[];
  laps: number[];
  /** Metres behind the leader, by slot. */
  gaps: number[];
  bestLaps: number[];
  finished: boolean[];
  /** The rider the director camera is on, or null. */
  cameraSubject: number | null;
  /**
   * Seconds into each rider's current lap, by slot: 0 before their first lap starts and once
   * they finish. Optional so a game page from before it existed still reports.
   */
  lapTimes?: number[];
}

export type MiniMotoGameToRoomMessage =
  | { type: 'ready'; version: number }
  | { type: 'error'; message: string }
  | MiniMotoPoseMessage
  | { type: 'events'; raceId: string; events: MiniMotoRaceEvent[] }
  | ({ type: 'standings' } & MiniMotoStandings)
  | { type: 'key'; code: string; down: boolean }
  /** The host added a map in the game's picker; the room makes it everyone's track. */
  | { type: 'course'; tiles: MiniMotoTile[] }
  /** The game page was hidden or shown; the room tells the host what a hidden window costs (#4584). */
  | { type: 'visibility'; hidden: boolean }
  /** The gunner's turret: aim at ~10 Hz and every shot, for the room to fan out to peers (#4586). */
  | ({ type: 'cannon-aim' } & MiniMotoCannonAim)
  /** The gunner's client saw its foam land on a rider. */
  | { type: 'cannon-hit'; slot: number };

// ---------------------------------------------------------------------------
// Foam cannon (#4586): spectators take turns at the game's foam turret.

export const MINI_MOTO_CANNON_TURN_MS = 20_000;
export const MINI_MOTO_CANNON_SHOTS_PER_TURN = 3;
/** A rider cannot be foamed again for this long. */
export const MINI_MOTO_CANNON_IMMUNITY_MS = 5_000;
/** How long a foamed rider slides and wobbles. */
export const MINI_MOTO_CANNON_SPIN_SECONDS = 1.5;

/** The turret's aim: yaw and pitch in radians, and whether the trigger fired on this sample. */
export interface MiniMotoCannonAim {
  seq: number;
  yaw: number;
  pitch: number;
  fire: boolean;
}

export interface MiniMotoCannonGunner {
  sessionId: string;
  name: string;
  /** Server epoch ms when the turn ends. */
  until: number;
  shotsLeft: number;
}

export interface MiniMotoCannonState {
  /** The host's setting; off hides the cannon card. */
  enabled: boolean;
  gunner: MiniMotoCannonGunner | null;
  queue: { sessionId: string; name: string }[];
}

/** server -> room: a foam hit, for the splat, the spin-out on the owner, and the caption. */
export interface MiniMotoCannonHitPayload {
  roomId: string;
  raceId: string;
  slot: number;
  bySessionId: string;
  byName: string;
  at: number;
}

// Aim packets on the peer data channel, like poses (magic 0xe2): 0xe3,
// version, flags (bit 0 fire), pad, race key u32, seq u32, yaw f32, pitch f32.
export const MINI_MOTO_CANNON_MAGIC = 0xe3;
export const MINI_MOTO_CANNON_PACKET_BYTES = 20;

export function encodeMiniMotoCannonAim(raceId: string, aim: MiniMotoCannonAim): Uint8Array {
  const out = new Uint8Array(MINI_MOTO_CANNON_PACKET_BYTES);
  const v = new DataView(out.buffer);
  out[0] = MINI_MOTO_CANNON_MAGIC;
  out[1] = 1;
  out[2] = aim.fire ? 1 : 0;
  v.setUint32(4, miniMotoRaceKey(raceId), true);
  v.setUint32(8, aim.seq >>> 0, true);
  v.setFloat32(12, aim.yaw, true);
  v.setFloat32(16, aim.pitch, true);
  return out;
}

export function isMiniMotoCannonPacket(bytes: Uint8Array): boolean {
  return bytes.byteLength === MINI_MOTO_CANNON_PACKET_BYTES && bytes[0] === MINI_MOTO_CANNON_MAGIC;
}

export function decodeMiniMotoCannonAim(bytes: Uint8Array): (MiniMotoCannonAim & { raceKey: number }) | null {
  if (!isMiniMotoCannonPacket(bytes) || bytes[1] !== 1) return null;
  const v = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  return { raceKey: v.getUint32(4, true), seq: v.getUint32(8, true), yaw: v.getFloat32(12, true), pitch: v.getFloat32(16, true), fire: (bytes[2] & 1) === 1 };
}

// ---------------------------------------------------------------------------
// Parsers. Each returns the message or null; nothing is coerced.

type Rec = Record<string, unknown>;
const isRec = (v: unknown): v is Rec => typeof v === 'object' && v !== null && !Array.isArray(v);
const isFiniteNum = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);
const isInt = (v: unknown, min: number, max: number): v is number =>
  typeof v === 'number' && Number.isInteger(v) && v >= min && v <= max;
const isSlot = (v: unknown): v is number => isInt(v, 0, MINI_MOTO_GRID_SIZE - 1);
const isRaceId = (v: unknown): v is string => typeof v === 'string' && RACE_ID.test(v);
const isVec = (v: unknown, n: number, limit: number): v is number[] =>
  Array.isArray(v) && v.length === n && v.every(x => isFiniteNum(x) && Math.abs(x) <= limit);
const isClock = (v: unknown): v is number => isFiniteNum(v) && v >= 0 && v <= CLOCK_LIMIT;
const isShortText = (v: unknown, max: number): v is string => typeof v === 'string' && v.length <= max;

/**
 * A display name made safe for the game's HUD and the booth's speech: control
 * characters and markup brackets removed, whitespace collapsed, length capped.
 */
export function sanitizeMiniMotoRiderName(raw: unknown, fallback = 'Rider'): string {
  if (typeof raw !== 'string') return fallback;
  const cleaned = raw
    .normalize('NFC')
    .replace(UNSAFE_NAME_CHARS, '')
    .replace(/\s+/g, ' ')
    .trim();
  const chars = Array.from(cleaned);
  const capped = chars.slice(0, MINI_MOTO_RIDER_NAME_MAX).join('').trim();
  return capped || fallback;
}

const GRID_LIMIT = MINI_MOTO_MAX_TILES - 1;

/** A track: 1..25 unique tiles, first at 0,0, known themes. Adjacency is the game's to enforce. */
export function parseMiniMotoCourse(v: unknown): MiniMotoTile[] | null {
  if (!Array.isArray(v) || v.length < 1 || v.length > MINI_MOTO_MAX_TILES) return null;
  const seen = new Set<string>();
  const out: MiniMotoTile[] = [];
  for (const t of v) {
    if (!isRec(t) || !isInt(t.gx, -GRID_LIMIT, GRID_LIMIT) || !isInt(t.gz, -GRID_LIMIT, GRID_LIMIT)) return null;
    if (typeof t.theme !== 'string' || !Object.prototype.hasOwnProperty.call(MINI_MOTO_THEMES, t.theme)) return null;
    const key = `${t.gx},${t.gz}`;
    if (seen.has(key)) return null;
    seen.add(key);
    out.push({ gx: t.gx, gz: t.gz, theme: t.theme as MiniMotoTheme });
  }
  if (out[0].gx !== 0 || out[0].gz !== 0) return null;
  return out;
}

export function parseMiniMotoVehicles(v: unknown): MiniMotoVehicles | null {
  if (!isRec(v) || typeof v.mode !== 'string' || typeof v.scale !== 'string') return null;
  if (!Object.prototype.hasOwnProperty.call(MINI_MOTO_VEHICLE_MODES, v.mode)) return null;
  if (!Object.prototype.hasOwnProperty.call(MINI_MOTO_VEHICLE_SCALES, v.scale)) return null;
  return { mode: v.mode as MiniMotoVehicleMode, scale: v.scale as MiniMotoVehicleScale };
}

const ANGLE_LIMIT = 10;
export function parseMiniMotoCannonAim(m: unknown): MiniMotoCannonAim | null {
  if (!isRec(m) || !isInt(m.seq, 0, Number.MAX_SAFE_INTEGER)) return null;
  if (!isFiniteNum(m.yaw) || !isFiniteNum(m.pitch) || Math.abs(m.yaw) > ANGLE_LIMIT || Math.abs(m.pitch) > ANGLE_LIMIT) return null;
  if (typeof m.fire !== 'boolean') return null;
  return { seq: m.seq, yaw: m.yaw, pitch: m.pitch, fire: m.fire };
}

/** Same tiles, same order, same themes. */
export function sameMiniMotoCourse(a: readonly MiniMotoTile[], b: readonly MiniMotoTile[]): boolean {
  return a.length === b.length && a.every((t, i) => t.gx === b[i].gx && t.gz === b[i].gz && t.theme === b[i].theme);
}

function parsePose(m: Rec): MiniMotoPoseMessage | null {
  if (!isRaceId(m.raceId) || !isSlot(m.slot) || !isInt(m.seq, 1, Number.MAX_SAFE_INTEGER) || !isClock(m.t)) return null;
  if (!isVec(m.p, 3, COORD_LIMIT) || !isVec(m.v, 3, SPEED_LIMIT) || !isVec(m.q, 4, 1.001)) return null;
  const q = m.q as number[];
  const norm = Math.hypot(q[0], q[1], q[2], q[3]);
  if (norm < 0.9 || norm > 1.1) return null;
  if (!isInt(m.lap, 0, 99) || !isFiniteNum(m.distance) || Math.abs(m.distance) > 1_000_000) return null;
  if (typeof m.finished !== 'boolean' || !isClock(m.finishTime)) return null;
  return {
    type: 'pose',
    raceId: m.raceId,
    slot: m.slot,
    seq: m.seq,
    t: m.t,
    p: [...(m.p as number[])] as [number, number, number],
    q: [q[0], q[1], q[2], q[3]],
    v: [...(m.v as number[])] as [number, number, number],
    lap: m.lap,
    distance: m.distance,
    finished: m.finished,
    finishTime: m.finishTime,
  };
}

function parseSlots(v: unknown): MiniMotoSlot[] | null {
  if (!Array.isArray(v) || v.length !== MINI_MOTO_GRID_SIZE) return null;
  const seen = new Set<number>();
  const out: MiniMotoSlot[] = [];
  let humans = 0;
  for (const s of v) {
    if (!isRec(s) || !isSlot(s.slot) || seen.has(s.slot)) return null;
    if (s.kind !== 'human' && s.kind !== 'ai') return null;
    if (typeof s.name !== 'string' || sanitizeMiniMotoRiderName(s.name, '') !== s.name || !s.name) return null;
    seen.add(s.slot);
    if (s.kind === 'human') humans++;
    out.push({ slot: s.slot, kind: s.kind, name: s.name });
  }
  if (humans > MINI_MOTO_MAX_HUMANS) return null;
  return out.sort((a, b) => a.slot - b.slot);
}

export function parseMiniMotoRoomToGame(data: unknown): MiniMotoRoomToGameMessage | null {
  if (!isRec(data) || typeof data.type !== 'string') return null;
  switch (data.type) {
    case 'pose':
      return parsePose(data);
    case 'abort':
      return isRaceId(data.raceId) ? { type: 'abort', raceId: data.raceId } : null;
    case 'tool':
      return typeof data.remove === 'boolean' ? { type: 'tool', remove: data.remove } : null;
    case 'look': {
      const look = parseMiniMotoLook(data.look);
      return look ? { type: 'look', look } : null;
    }
    case 'lite':
      return typeof data.on === 'boolean' ? { type: 'lite', on: data.on } : null;
    case 'render':
      return typeof data.on === 'boolean' ? { type: 'render', on: data.on } : null;
    case 'cannon':
      return typeof data.gunner === 'boolean' ? { type: 'cannon', gunner: data.gunner } : null;
    case 'cannon-replay': {
      const aim = parseMiniMotoCannonAim(data);
      return aim ? { type: 'cannon-replay', ...aim } : null;
    }
    case 'foam':
      return isSlot(data.slot) ? { type: 'foam', slot: data.slot } : null;
    case 'chase':
      return isMiniMotoChaseDistance(data.distance) ? { type: 'chase', distance: data.distance } : null;
    case 'steer':
      return typeof data.value === 'number' && Number.isFinite(data.value)
        ? { type: 'steer', value: Math.max(-1, Math.min(1, data.value)) }
        : null;
    case 'drive':
      return (MINI_MOTO_DRIVE_INPUTS as readonly unknown[]).includes(data.input) && typeof data.held === 'boolean'
        ? { type: 'drive', input: data.input as MiniMotoDriveInput, held: data.held }
        : null;
    case 'lobby': {
      const course = parseMiniMotoCourse(data.course);
      const vehicles = parseMiniMotoVehicles(data.vehicles);
      if (!course || !vehicles || typeof data.editor !== 'boolean') return null;
      return { type: 'lobby', course, vehicles, editor: data.editor };
    }
    case 'race': {
      if (!isRaceId(data.raceId) || !isInt(data.seed, 0, 0xffffffff)) return null;
      if (!(MINI_MOTO_LAP_OPTIONS as readonly unknown[]).includes(data.laps)) return null;
      if (!isFiniteNum(data.gateInMs) || data.gateInMs < -MAX_GATE_ELAPSED_MS || data.gateInMs > MAX_GATE_DELAY_MS) return null;
      const slots = parseSlots(data.slots);
      if (!slots) return null;
      const course = parseMiniMotoCourse(data.course);
      if (!course) return null;
      const vehicles = parseMiniMotoVehicles(data.vehicles);
      if (!vehicles) return null;
      if (data.localSlot !== null && !isSlot(data.localSlot)) return null;
      // A client can only ride a human slot.
      if (data.localSlot !== null && slots[data.localSlot as number].kind !== 'human') return null;
      if (typeof data.authority !== 'boolean') return null;
      if (data.camera !== 'ride' && data.camera !== 'follow' && data.camera !== 'battle') return null;
      if (data.camera === 'ride' && data.localSlot === null) return null;
      const watchSlot = data.watchSlot ?? null;
      if (watchSlot !== null && (!isSlot(watchSlot) || slots[watchSlot as number].kind !== 'human')) return null;
      if (!Array.isArray(data.forwardKeys) || data.forwardKeys.length > MAX_FORWARD_KEYS) return null;
      if (!data.forwardKeys.every(k => typeof k === 'string' && KEY_CODE.test(k))) return null;
      return {
        type: 'race',
        raceId: data.raceId,
        seed: data.seed,
        laps: data.laps as MiniMotoLaps,
        gateInMs: data.gateInMs,
        slots,
        course,
        vehicles,
        localSlot: data.localSlot as number | null,
        authority: data.authority,
        camera: data.camera,
        watchSlot: watchSlot as number | null,
        forwardKeys: [...(data.forwardKeys as string[])],
      };
    }
    default:
      return null;
  }
}

function parseEvent(e: unknown): MiniMotoRaceEvent | null {
  if (!isRec(e) || !(MINI_MOTO_RACE_EVENT_KINDS as readonly unknown[]).includes(e.kind)) return null;
  if (!isClock(e.t) || !isInt(e.slot, -1, MINI_MOTO_GRID_SIZE - 1)) return null;
  const out: MiniMotoRaceEvent = { kind: e.kind as MiniMotoRaceEventKind, t: e.t, slot: e.slot };
  if (e.source !== undefined) { if (!isShortText(e.source, 24)) return null; out.source = e.source; }
  if (e.grade !== undefined) { if (!isShortText(e.grade, 24)) return null; out.grade = e.grade; }
  if (e.text !== undefined) { if (!isShortText(e.text, MESSAGE_MAX)) return null; out.text = e.text; }
  if (e.tier !== undefined) { if (!isInt(e.tier, 0, 10)) return null; out.tier = e.tier; }
  if (e.lap !== undefined) { if (!isInt(e.lap, 0, 99)) return null; out.lap = e.lap; }
  for (const k of ['lapTime', 'bestLap', 'finishTime'] as const) {
    if (e[k] !== undefined) { if (!isClock(e[k])) return null; out[k] = e[k] as number; }
  }
  return out;
}

function perSlot<T>(v: unknown, ok: (x: unknown) => x is T): v is T[] {
  return Array.isArray(v) && v.length === MINI_MOTO_GRID_SIZE && v.every(ok);
}

export function parseMiniMotoGameToRoom(data: unknown): MiniMotoGameToRoomMessage | null {
  if (!isRec(data) || typeof data.type !== 'string') return null;
  switch (data.type) {
    case 'ready':
      return isInt(data.version, 1, 1000) ? { type: 'ready', version: data.version } : null;
    case 'error':
      return isShortText(data.message, MESSAGE_MAX) ? { type: 'error', message: data.message } : null;
    case 'pose':
      return parsePose(data);
    case 'course': {
      const tiles = parseMiniMotoCourse(data.tiles);
      return tiles ? { type: 'course', tiles } : null;
    }
    case 'visibility':
      return typeof data.hidden === 'boolean' ? { type: 'visibility', hidden: data.hidden } : null;
    case 'cannon-aim': {
      const aim = parseMiniMotoCannonAim(data);
      return aim ? { type: 'cannon-aim', ...aim } : null;
    }
    case 'cannon-hit':
      return isSlot(data.slot) ? { type: 'cannon-hit', slot: data.slot } : null;
    case 'key':
      return typeof data.code === 'string' && KEY_CODE.test(data.code) && typeof data.down === 'boolean'
        ? { type: 'key', code: data.code, down: data.down }
        : null;
    case 'events': {
      if (!isRaceId(data.raceId) || !Array.isArray(data.events) || data.events.length > MAX_EVENTS_PER_BATCH) return null;
      const events: MiniMotoRaceEvent[] = [];
      for (const e of data.events) {
        const parsed = parseEvent(e);
        if (!parsed) return null;
        events.push(parsed);
      }
      return { type: 'events', raceId: data.raceId, events };
    }
    case 'standings': {
      if (!isRaceId(data.raceId) || !isClock(data.t)) return null;
      const order = data.order;
      if (!Array.isArray(order) || order.length !== MINI_MOTO_GRID_SIZE || !order.every(isSlot)) return null;
      if (new Set(order).size !== MINI_MOTO_GRID_SIZE) return null;
      if (!perSlot(data.laps, (x): x is number => isInt(x, 0, 99))) return null;
      if (!perSlot(data.gaps, (x): x is number => isFiniteNum(x) && x >= 0 && x < 1_000_000)) return null;
      if (!perSlot(data.bestLaps, isClock)) return null;
      if (!perSlot(data.finished, (x): x is boolean => typeof x === 'boolean')) return null;
      if (data.cameraSubject !== null && !isSlot(data.cameraSubject)) return null;
      if (data.lapTimes !== undefined && !perSlot(data.lapTimes, isClock)) return null;
      return {
        type: 'standings',
        raceId: data.raceId,
        t: data.t,
        order: [...order],
        laps: [...data.laps],
        gaps: [...data.gaps],
        bestLaps: [...data.bestLaps],
        finished: [...data.finished],
        cameraSubject: data.cameraSubject as number | null,
        ...(data.lapTimes !== undefined ? { lapTimes: [...(data.lapTimes as number[])] } : {}),
      };
    }
    default:
      return null;
  }
}

// ---------------------------------------------------------------------------
// Room <-> signaling (#4577 Phase 2). The race manager in signaling is the
// source of truth for who rides, the seed, the gate time and the results.

export const MINI_MOTO_NAME = 'Mini Moto';
/** Must match packages/mini-moto/assets.json `version`; the CDN path is games/mini-moto/<version>. */
export const MINI_MOTO_ASSETS_VERSION = 'v1';
/** Time on the grid before the gate drops: long enough for the booth's intro. */
export const MINI_MOTO_GRID_HOLD_MS = 15_000;
/** No plausible lap is quicker than this; a finish implying one is rejected. */
export const MINI_MOTO_MIN_LAP_SECONDS = 15;
/** A race still running this long per lap (plus a margin) is closed with what it has. */
export const MINI_MOTO_MAX_LAP_SECONDS = 120;

export const MINI_MOTO_EVENTS = {
  /** client -> server: join the table and get the state. Payload { roomId }. */
  SNAPSHOT: 'mini-moto:snapshot',
  /** client -> server: a MiniMotoAction, with an ack. */
  ACTION: 'mini-moto:action',
  /** client -> server: leave the table. Payload { roomId }. */
  LEAVE: 'mini-moto:leave',
  /** server -> room: MiniMotoRoomState. */
  STATE: 'mini-moto:state',
  /** server -> one socket: { message }. */
  ERROR: 'mini-moto:error',
  /** server -> room: MiniMotoBoothCaption, as a commentary line starts playing (Phase 4). */
  BOOTH: 'mini-moto:booth',
  /** server -> room: MiniMotoCannonHitPayload, a validated foam hit (#4586). */
  CANNON_HIT: 'mini-moto:cannon-hit',
  /**
   * Connected TV pose relay. client -> server: MiniMotoPoseRelay from a slot's owner, sent only while
   * the room's TV display is on. server -> observers only: MiniMotoRelayedPose. A TV is not in the
   * peer mesh, so without this it would draw the track with no bikes on it.
   */
  POSE: 'mini-moto:pose',
} as const;

/**
 * Pose packets (encodeMiniMotoPose) for the TV relay: everything a client sends in one pose tick, as
 * one message. Socket.IO drops a volatile message while the previous one is still being written, so
 * the race authority's eight AI poses sent one by one reached the TV at 3 Hz each, not 20. `packet`
 * is the single-pose form sent before batching.
 */
export interface MiniMotoPoseRelay {
  roomId: string;
  packets?: Array<Uint8Array | ArrayBuffer>;
  packet?: Uint8Array | ArrayBuffer;
}

/** What an observer receives: the packets and the session signaling verified owns their slots. */
export interface MiniMotoRelayedPose {
  roomId: string;
  from: string;
  packets?: Array<Uint8Array | ArrayBuffer>;
  packet?: Uint8Array | ArrayBuffer;
}

/** What the companion just said about the race, for the room's caption. */
export interface MiniMotoBoothCaption {
  roomId: string;
  raceId: string;
  text: string;
  /** Server epoch ms. */
  at: number;
}

export type MiniMotoPhase = 'lobby' | 'grid' | 'racing' | 'finished';

export interface MiniMotoRider {
  sessionId: string;
  name: string;
  /** The rider's game has loaded and reported ready. */
  loaded: boolean;
}

export interface MiniMotoRaceSlot extends MiniMotoSlot {
  /** The rider's session for a human slot; null for AI. */
  sessionId: string | null;
}

export interface MiniMotoRace {
  raceId: string;
  seed: number;
  laps: MiniMotoLaps;
  /** Server epoch ms when the gate drops. */
  gateAt: number;
  slots: MiniMotoRaceSlot[];
  /** The track and vehicles as they were when the race started. */
  course: MiniMotoTile[];
  vehicles: MiniMotoVehicles;
  /** The session that simulates the AI riders and reports standings (the host who started it). */
  authoritySessionId: string;
}

export interface MiniMotoResult {
  slot: number;
  name: string;
  sessionId: string | null;
  /** 1-based; null for a rider who did not finish. */
  place: number | null;
  /** Race-clock seconds, as reported by the rider's own client; null if not finished. */
  finishTime: number | null;
}

export interface MiniMotoRoomState {
  roomId: string;
  phase: MiniMotoPhase;
  /** Increments on every change; clients drop anything older. */
  revision: number;
  /** Server epoch ms when this state was sent. */
  serverNow: number;
  laps: MiniMotoLaps;
  /** The room's track and vehicles (#4584): set by the host between races, shared by everyone. */
  course: MiniMotoTile[];
  vehicles: MiniMotoVehicles;
  riders: MiniMotoRider[];
  cannon: MiniMotoCannonState;
  race: MiniMotoRace | null;
  /** The authority's latest standings for the current race, or null. */
  standings: MiniMotoStandings | null;
  results: MiniMotoResult[] | null;
  /** Why the last race was called off, if it was. */
  redFlag: string | null;
  /**
   * An observer (a Connected TV, a program publisher) is at the table. Slot owners copy their poses to
   * signaling's observer relay while it is true: observers are not in the peer mesh. Set by the server
   * from who has joined, so it does not depend on any one phone knowing a TV is on.
   */
  watched?: boolean;
  /** How far the room's TV camera rides behind the person racing; anyone in the room may change it. */
  tvChase?: MiniMotoChaseDistance;
  /**
   * A TV at the table is still loading the game: the host's start waits for it, as for riders, for at
   * most a minute after the TV sat down.
   */
  tvWaiting?: boolean;
  /** The companion calls the race aloud. The host switches it, mid-race too; on by default. */
  commentary: boolean;
}

export type MiniMotoAction =
  | { type: 'ride' }
  | { type: 'unride' }
  | { type: 'loaded' }
  | { type: 'configure'; laps?: MiniMotoLaps; vehicles?: MiniMotoVehicles; cannon?: boolean }
  | { type: 'cannon-join' }
  | { type: 'cannon-leave' }
  /** The gunner fired (counts a shot) or saw its foam land on `slot`. */
  | { type: 'cannon-shot' }
  | { type: 'cannon-hit'; slot: number }
  | { type: 'tv-chase'; distance: MiniMotoChaseDistance }
  /** The host switches the companion's play-by-play on or off, at any phase. */
  | { type: 'commentary'; on: boolean }
  | { type: 'course'; tiles: MiniMotoTile[] }
  | { type: 'start' }
  | { type: 'abort' }
  | { type: 'reset' }
  | { type: 'events'; raceId: string; events: MiniMotoRaceEvent[] }
  | ({ type: 'standings' } & MiniMotoStandings);

/** Client -> server action, with the room it is for. Everything else is dropped. */
export function parseMiniMotoAction(data: unknown): { roomId: string; action: MiniMotoAction } | null {
  if (!isRec(data) || typeof data.roomId !== 'string' || !data.roomId || data.roomId.length > 128) return null;
  const roomId = data.roomId;
  switch (data.type) {
    case 'ride':
    case 'unride':
    case 'loaded':
    case 'start':
    case 'abort':
    case 'reset':
    case 'cannon-join':
    case 'cannon-leave':
    case 'cannon-shot':
      return { roomId, action: { type: data.type } };
    case 'cannon-hit':
      return isSlot(data.slot) ? { roomId, action: { type: 'cannon-hit', slot: data.slot } } : null;
    case 'tv-chase':
      return isMiniMotoChaseDistance(data.distance) ? { roomId, action: { type: 'tv-chase', distance: data.distance } } : null;
    case 'commentary':
      return typeof data.on === 'boolean' ? { roomId, action: { type: 'commentary', on: data.on } } : null;
    case 'configure': {
      const action: Extract<MiniMotoAction, { type: 'configure' }> = { type: 'configure' };
      if (data.laps !== undefined) {
        if (!(MINI_MOTO_LAP_OPTIONS as readonly unknown[]).includes(data.laps)) return null;
        action.laps = data.laps as MiniMotoLaps;
      }
      if (data.vehicles !== undefined) {
        const vehicles = parseMiniMotoVehicles(data.vehicles);
        if (!vehicles) return null;
        action.vehicles = vehicles;
      }
      if (data.cannon !== undefined) {
        if (typeof data.cannon !== 'boolean') return null;
        action.cannon = data.cannon;
      }
      return action.laps || action.vehicles || action.cannon !== undefined ? { roomId, action } : null;
    }
    case 'course': {
      const tiles = parseMiniMotoCourse(data.tiles);
      return tiles ? { roomId, action: { type: 'course', tiles } } : null;
    }
    case 'events':
    case 'standings': {
      const parsed = parseMiniMotoGameToRoom({ ...data, type: data.type });
      if (!parsed || (parsed.type !== 'events' && parsed.type !== 'standings')) return null;
      return { roomId, action: parsed };
    }
    default:
      return null;
  }
}

// ---------------------------------------------------------------------------
// Pose packets on the peer data channel (#4577 Phase 3). Binary, like the
// participant expression frames (#3842, magic 0xe1), so 20 Hz per rider costs
// 68 bytes rather than ~300 of JSON. Little-endian:
//
//   0 magic 0xe2 · 1 version · 2 slot · 3 flags (bit 0 finished)
//   4 race key u32 · 8 seq u32 · 12 t f32 · 16 p f32×3 · 28 q f32×4
//   44 v f32×3 · 56 lap u8 · 57..59 zero · 60 distance f32 · 64 finishTime f32
//
// The packet carries a 32-bit key of the race id, not the id itself; the
// receiver matches it against the race it is in. Decoded poses still go
// through parseMiniMotoRoomToGame before the game sees them.

export const MINI_MOTO_POSE_MAGIC = 0xe2;
export const MINI_MOTO_POSE_VERSION = 1;
export const MINI_MOTO_POSE_PACKET_BYTES = 68;

/** FNV-1a of the race id: enough to tell this race's packets from a previous one's. */
export function miniMotoRaceKey(raceId: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < raceId.length; i++) {
    h ^= raceId.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h >>> 0;
}

export function encodeMiniMotoPose(pose: MiniMotoPose): Uint8Array {
  const out = new Uint8Array(MINI_MOTO_POSE_PACKET_BYTES);
  const v = new DataView(out.buffer);
  out[0] = MINI_MOTO_POSE_MAGIC;
  out[1] = MINI_MOTO_POSE_VERSION;
  out[2] = pose.slot & 0xff;
  out[3] = pose.finished ? 1 : 0;
  v.setUint32(4, miniMotoRaceKey(pose.raceId), true);
  v.setUint32(8, pose.seq >>> 0, true);
  v.setFloat32(12, pose.t, true);
  pose.p.forEach((x, i) => v.setFloat32(16 + i * 4, x, true));
  pose.q.forEach((x, i) => v.setFloat32(28 + i * 4, x, true));
  pose.v.forEach((x, i) => v.setFloat32(44 + i * 4, x, true));
  out[56] = Math.max(0, Math.min(255, pose.lap | 0));
  v.setFloat32(60, pose.distance, true);
  v.setFloat32(64, pose.finishTime, true);
  return out;
}

export function isMiniMotoPosePacket(bytes: Uint8Array): boolean {
  return bytes.byteLength === MINI_MOTO_POSE_PACKET_BYTES && bytes[0] === MINI_MOTO_POSE_MAGIC;
}

/** A decoded packet: the pose without its race id, plus the key to match it against. */
export type MiniMotoPosePacket = Omit<MiniMotoPose, 'raceId'> & { raceKey: number };

/** Decode, or null for a wrong version or size. Values are checked later by the parser. */
export function decodeMiniMotoPose(bytes: Uint8Array): MiniMotoPosePacket | null {
  if (!isMiniMotoPosePacket(bytes) || bytes[1] !== MINI_MOTO_POSE_VERSION) return null;
  const v = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const f = (o: number) => v.getFloat32(o, true);
  return {
    raceKey: v.getUint32(4, true),
    slot: bytes[2],
    seq: v.getUint32(8, true),
    t: f(12),
    p: [f(16), f(20), f(24)],
    q: [f(28), f(32), f(36), f(40)],
    v: [f(44), f(48), f(52)],
    lap: bytes[56],
    distance: f(60),
    finished: (bytes[3] & 1) === 1,
    finishTime: f(64),
  };
}
